import { useMemo, useState } from "react";
import {
    Box, Button, Chip, Collapse, Stack, Typography, useTheme,
} from "@mui/material";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutlined";
import FrostedCard from "src/components/quote/FrostedCard";
import { amberCtaSx } from "src/components/quote/amberCtaSx";
import MessageThreadDialog from "src/components/common/MessageThreadDialog";
import { useAuth } from "src/context/AuthContext";
import { useLang, useT } from "src/i18n";
import { API_URL } from "src/utils/apiUrl";
import {
    formatPriceEuros, formatNettoEuros, formatUnitPriceEurPerM3, formatDistanceKm, formatSentDate,
    truncateAtWord, localizeDecimal,
} from "src/utils/valut/valutFormatters";
import { offerStateLabel } from "@ibetoni/jerry-client";
import { gate2Fallback, resolvePeruutusehdot } from "src/utils/legal/acceptanceCopy";

function FactRow({ icon, label, value, hint }) {
    return (
        <Stack direction="row" spacing={1.25} sx={{ alignItems: "flex-start", py: 0.75 }}>
            <Box sx={{ width: 18, color: "text.secondary", lineHeight: 1.4 }}>{icon}</Box>
            <Box sx={{ flex: 1 }}>
                <Typography variant="caption" sx={{ color: "text.secondary", display: "block" }}>
                    {label}
                </Typography>
                <Typography variant="body2">{value || "—"}</Typography>
                {hint && (
                    <Typography variant="caption" sx={{ color: "text.secondary", display: "block" }}>
                        {hint}
                    </Typography>
                )}
            </Box>
        </Stack>
    );
}

/**
 * Customer-facing offer card on /valut/:id. **Legally load-bearing surface** —
 * every contract-critical field is rendered on the main card, NOT behind the
 * "Lue lisää" expander. KKV transparency rule for marketplace acceptance:
 * company name (h5, bold) + total price (incl. ALV with netto split) +
 * cancellation-terms summary + validity are all visible by default. The
 * "Lue lisää" expander only adds the full cancellation text + the provider's
 * extra notes — never a contract-critical field.
 *
 * **Y-tunnus is NOT shown on this selection card.** Company name + price are
 * the two primary anchors at the selection stage; full provider identity
 * (incl. Y-tunnus where available) belongs on the binding-acceptance surface
 * (`AcceptConfirmModal` / `ValuAcceptedScreen`). The company introduction
 * (`offer.companyDescription`) surfaces as a truncated "Lisää…" snippet under
 * the company name and does not repeat in a separate "Yrityksestä" box.
 *
 * Primary CTA: "HYVÄKSY TARJOUS" with the caption
 * "Sitova tilaus ja pumppausaika sovitaan puhelimitse hyväksynnän jälkeen"
 * (fb#454: "JA TILAA" was dropped so the button no longer claims an order is
 * placed at the click). Acceptance here is not a binding payment moment — the
 * actual contract is agreed by phone after the provider has been notified —
 * so the caption sets that expectation rather than asserting a payment
 * obligation. The
 * `aria-label` mirrors the visible caption so screen readers announce it
 * as one label. Keep the visible caption and aria-label in sync, and see
 * the betonijerry `CLAUDE.md` "Legal UX conventions" section before changing.
 *
 * State machine driven by `offer.status` + `requestStatus`:
 *   pending  + request live              → full card, primary CTA enabled
 *   accepted                             → amber confirmation strip, no CTA
 *   pending  + request not live          → card visible, CTA hidden (sibling
 *                                          won, or request expired; card stays
 *                                          for context)
 *   rejected | expired | withdrawn       → dimmed (0.55 opacity + grayscale),
 *                                          status chip, no CTA
 *
 * Acceptance handshake: clicking the primary CTA calls `onAccept(offer)` — the
 * parent (ValuDetail.jsx) opens AcceptConfirmModal which then runs the actual
 * POST /accept after user re-confirmation.
 *
 * Props:
 *   - offer:         Offer row from GET /:id/offers. The component uses:
 *                    companyDescription (truncated snippet under name),
 *                    jerryContactName / jerryContactPhone /
 *                    openingHours (revealed by backend only on status='accepted' —
 *                    null on all pending/rejected offers; do not show on pre-accept cards).
 *   - onAccept:      Called with `offer` when the user clicks the primary CTA.
 *                    Parent decides what happens next (modal, POST, …).
 *   - accepting:     `true` while THIS offer's accept POST is in flight —
 *                    disables the primary CTA to prevent double-submit. Scoped
 *                    per-card by the parent (only true for the in-flight offer).
 *   - requestStatus / requestExpiresAt: Parent pumppuRequest's status and expiry.
 *                    The CTA shows only while the request is live (status 'open'
 *                    and not past expiresAt) — a sibling win or expiry hides it.
 */
export default function OfferCard({ offer, onAccept, accepting = false, requestStatus, requestExpiresAt, maaraM3, copy }) {
    const theme = useTheme();
    const w = theme.custom?.wizard ?? {};
    const easing = w.motion?.easing ?? "cubic-bezier(0.22, 1, 0.36, 1)";
    const { token, user } = useAuth();
    const { lang } = useLang();
    const t = useT();
    // Defensive fallback — ValuDetail always passes a real copy (from its own
    // useAcceptanceCopy hook), so this only guards a caller that forgets to.
    const fallbackCopy = useMemo(() => gate2Fallback(t), [t]);
    const effectiveCopy = copy ?? fallbackCopy;
    const customerPersonId = user?.person?.[0]?.personId;
    const [expanded, setExpanded] = useState(false);
    const [chatOpen, setChatOpen] = useState(false);
    const [introExpanded, setIntroExpanded] = useState(false);

    const isPending = offer.status === "pending";
    const isAccepted = offer.status === "accepted";
    // Only a live request's offers are acceptable (fb#2317; the backend enforces it too).
    const requestLive = requestStatus === "open"
        && !(requestExpiresAt && new Date(requestExpiresAt) <= new Date());
    const dimmed = !isPending && !isAccepted;

    const totalPrice = formatPriceEuros(offer.priceCents, offer.vatPercent, lang);
    const nettoPrice = formatNettoEuros(offer.priceCents, lang);
    const unitPrice = formatUnitPriceEurPerM3(offer.priceCents, maaraM3, lang);
    const dist = formatDistanceKm(offer.providerDistanceKm, lang);
    // Platform-standard cancellation terms — same for every offer. Sourced from the
    // DB acceptance copy (BETONIJERRY_OFFER_ACCEPTANCE.peruutusehdot, editable via
    // `ib legal`); resolvePeruutusehdot falls back to the bundled standard if the
    // DB value is missing OR malformed (legally load-bearing — never blank).
    const peruutusehdot = resolvePeruutusehdot(effectiveCopy, t);
    const priceTerms = (offer.priceTerms || "").trim();
    const intro = (offer.companyDescription || "").trim();
    const introSnippet = truncateAtWord(intro, 140);
    const introHasMore = introSnippet !== intro;

    return (
        <FrostedCard
            sx={{
                p: 2.5,
                opacity: dimmed ? 0.55 : 1,
                filter: dimmed ? "grayscale(0.4)" : "none",
                position: "relative",
                // Each offer card rises in on mount. On first load the list arrives
                // together; on a push-driven refetch only a genuinely new offer
                // mounts (cards keyed by id), so this doubles as the "new offer
                // arrived" cue. Transform-only — leaves the dimmed/rejected opacity
                // untouched and never gates the card invisible.
                animation: `bjOfferIn 300ms ${easing}`,
                "@keyframes bjOfferIn": {
                    from: { transform: "translateY(7px)" },
                    to: { transform: "translateY(0)" },
                },
                "@media (prefers-reduced-motion: reduce)": { animation: "none" },
            }}
            aria-labelledby={`offer-${offer.pumppuOfferId}-name`}
        >
            <Stack direction="row" sx={{ alignItems: "flex-start", justifyContent: "space-between" }}>
                <Box sx={{ minWidth: 0 }}>
                    <Typography id={`offer-${offer.pumppuOfferId}-name`} variant="h5" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
                        {offer.asiakasNimi}
                    </Typography>
                    {intro && (
                        <Box sx={{ mt: 0.5 }}>
                            <Typography variant="body2" sx={{ color: "text.secondary", whiteSpace: "pre-wrap" }}>
                                {introExpanded ? intro : introSnippet}
                            </Typography>
                            {introHasMore && (
                                <Button size="small" onClick={() => setIntroExpanded((v) => !v)} sx={{ p: 0, minWidth: 0, textTransform: "none" }}>
                                    {introExpanded ? t("valut.offerCard.showLess") : t("valut.offerCard.readMoreIntro")}
                                </Button>
                            )}
                        </Box>
                    )}
                </Box>
                {!isPending && offerStateLabel(offer.status) && (
                    <Chip
                        size="small"
                        label={offerStateLabel(offer.status)}
                        color={offer.status === "accepted" || offer.status === "confirmed" ? "success" : "default"}
                    />
                )}
            </Stack>

            <Box sx={{ mt: 2 }}>
                <Typography variant="caption" sx={{ color: "text.secondary" }}>{t("valut.offerCard.priceEstimateLabel")}</Typography>
                <Typography
                    variant="h4"
                    sx={{ color: w.amber || theme.palette.warning.main, fontWeight: 700 }}
                    aria-label={t("valut.offerCard.priceAriaLabel", { total: totalPrice, vat: offer.vatPercent })}
                >
                    {totalPrice}
                </Typography>
                <Typography variant="caption" sx={{ color: "text.secondary", display: "block" }}>
                    {t("valut.offerCard.vatLine", { vat: localizeDecimal(offer.vatPercent, lang), netto: nettoPrice })}
                </Typography>
                {unitPrice && (
                    <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mt: 0.25 }}>
                        {t("valut.offerCard.unitPriceLine", { unitPrice })}
                    </Typography>
                )}
            </Box>

            {priceTerms && (
                <Box
                    sx={{
                        mt: 2,
                        p: 1.5,
                        // A px STRING, not a bare number — sx multiplies a bare number by
                        // shape.borderRadius (6), so `w.microRadius` (14) rendered 84px: a
                        // pill, rounder than the FrostedCard containing it. Same trap as
                        // fb#501's cookie bar, which was swept by token rather than pattern.
                        borderRadius: `${w.microRadius ?? 14}px`,
                        bgcolor: w.microBg ?? "rgba(36,56,66,0.65)",
                        border: `1px solid ${theme.palette.divider}`,
                    }}
                >
                    <Typography variant="caption" sx={{ color: "text.secondary", display: "block", fontWeight: 600, mb: 0.25 }}>
                        {t("valut.offerCard.priceTermsLabel")}
                    </Typography>
                    <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                        {priceTerms}
                    </Typography>
                </Box>
            )}

            <Box sx={{ mt: 2 }}>
                <FactRow icon="📍" label={t("valut.offerCard.locationLabel")} value={dist ? t("valut.offerCard.locationValue", { dist }) : null} />
                <FactRow icon="⏳" label={t("valut.offerCard.validUntilLabel")} value={offer.validUntil ? t("valut.offerCard.validUntilValue", { date: formatSentDate(offer.validUntil, lang) }) : "—"} />
                <FactRow
                    icon="⚖️"
                    label={t("valut.offerCard.cancellationLabel")}
                    value={peruutusehdot.summary}
                    hint={t("valut.offerCard.cancellationHint")}
                />
            </Box>

            <Button
                size="small"
                onClick={() => setExpanded((v) => !v)}
                sx={{ mt: 1, alignSelf: "flex-start" }}
            >
                {expanded ? t("valut.offerCard.readMoreCollapse") : t("valut.offerCard.readMoreExpand")}
            </Button>
            <Collapse in={expanded}>
                <Box sx={{ mt: 1, p: 1.5, bgcolor: "rgba(244,242,238,0.03)", borderRadius: 1 }}>
                    <Typography variant="caption" sx={{ color: "text.secondary" }}>{t("valut.offerCard.cancellationLabel")}</Typography>
                    <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", mb: offer.extraNotes ? 1 : 0 }}>
                        {peruutusehdot.full}
                    </Typography>
                    {offer.extraNotes && (
                        <>
                            <Typography variant="caption" sx={{ color: "text.secondary" }}>{t("valut.offerCard.extraNotesLabel")}</Typography>
                            <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                                {offer.extraNotes}
                            </Typography>
                        </>
                    )}
                </Box>
            </Collapse>

            {isPending && requestLive && (
                <Stack spacing={1} sx={{ mt: 3 }}>
                    <Button
                        variant="contained"
                        size="large"
                        fullWidth
                        sx={[amberCtaSx, { py: 1.5 }]}
                        onClick={() => onAccept(offer)}
                        disabled={accepting}
                        aria-label={effectiveCopy.ariaLabel}
                    >
                        <Stack spacing={0.25} sx={{ alignItems: "center", minWidth: 0 }}>
                            <span>{effectiveCopy.ctaLabel}</span>
                            <Typography variant="caption" sx={{ opacity: 0.85, textTransform: "none", whiteSpace: "normal", textAlign: "center", lineHeight: 1.3 }}>
                                {effectiveCopy.ctaCaption}
                            </Typography>
                        </Stack>
                    </Button>
                    {offer.messageThreadId && customerPersonId && (
                        <Button
                            variant="outlined"
                            startIcon={<ChatBubbleOutlineIcon />}
                            onClick={() => setChatOpen(true)}
                        >
                            {t("valut.offerCard.askViaChat")}
                        </Button>
                    )}
                </Stack>
            )}

            <MessageThreadDialog
                open={chatOpen}
                onClose={() => setChatOpen(false)}
                title={t("valut.offerCard.chatTitle", { name: offer.asiakasNimi })}
                threadId={offer.messageThreadId}
                currentPersonId={customerPersonId}
                token={token}
                apiBaseUrl={API_URL}
            />

            {isAccepted && (
                <Box
                    sx={{
                        mt: 3,
                        p: 1.5,
                        borderRadius: 1,
                        bgcolor: w.amber ? `${w.amber}22` : "rgba(232,137,59,0.13)",
                        border: `1px solid ${w.amber || theme.palette.warning.main}`,
                        textAlign: "center",
                    }}
                >
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {t("valut.offerCard.acceptedNotice", { date: formatSentDate(offer.updatedAt, lang) })}
                    </Typography>
                </Box>
            )}
        </FrostedCard>
    );
}
